import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import {
  urlIApiPaymentMethodProveedor,
  urlBackend,
} from '@/shared/constants/networking.constants';
import { AlertSeverityEnum, UseMutationsParams } from '@/shared/interfaces';
import {
  IPaymentMethodProveedor,
  IPaymentMethodProveedorDataRes,
} from '@/shared/interfaces/erp/system-settings';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

///* tanSctack query
export const useFetchPaymentMethodsProveedor = (
  page = 1,
  nombre = '',
  page_size = 10
) => {
  return useQuery({
    queryKey: ['paymentMethodsProveedor', page, nombre, page_size],
    queryFn: async () => {
      return await getPaymentMethodsProveedor({ page, nombre, page_size });
    },
  });
};

export const useFetchPaymentMethodsProveedorParams = (
  params?: getPaymentMethodsProveedorProps,
  enabled = true
) => {
  return useQuery({
    queryKey: ['paymentMethodsProveedor', ...Object.values(params ?? {})],

    queryFn: async () => {
      return await getPaymentMethodsProveedor(params);
    },

    enabled: enabled,
  });
};

export const useCreatePaymentMethodProveedor = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createPaymentMethodProveedor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentMethodsProveedor'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Metodo pago creado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdatePaymentMethodProveedor = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updatePaymentMethodProveedor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentMethodsProveedor'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Metodo pago actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useRemovePaymentMethodProveedor = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;

  return useMutation({
    mutationFn: removePaymentMethodProveedor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentMethods'] });
    },
    onSettled: () => {
      setConfirmDialogIsOpen(false);
    },
  });
};

export const useGetPaymentMethodProveedor = (id: string) => {
  return useQuery({
    queryKey: ['paymentMethod', id],
    queryFn: async () => {
      return getPaymentMethodProveedor(id);
    },
  });
};

///* axios
export type getPaymentMethodsProveedorProps = Partial<IPaymentMethodProveedor> & {
  page?: number;
  page_size?: number;

  entidad_financiera?: string; // code fk
  filterByState?: boolean;
};

export type CreatePaymentMethodProveedorProps = Omit<IPaymentMethodProveedor, 'id_formapago_proveedor '>;

export const getPaymentMethodsProveedor = async (props?: getPaymentMethodsProveedorProps) => {
  const stateParams = { ...props };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<IPaymentMethodProveedorDataRes>(
    `${urlBackend}api/v1/formapagoproveedor/?${queryParams}`,
    true
  );
};

export const getPaymentMethodProveedor = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<IPaymentMethodProveedor>(`${urlIApiPaymentMethodProveedor}${id}/`, true);

  res.status === 'error' &&
    setAlert(true, 'Método de pago no encontrado', 'error');

  return res;
};

export const createPaymentMethodProveedor = async (data: CreatePaymentMethodProveedorProps) => {
  //const setAlert = useUiStore.getState().setAlert;
  const res = await post<IPaymentMethodProveedor>(urlIApiPaymentMethodProveedor, data, true);

  //setAlert(true, res.message, res.status);
  return res;
};

export const updatePaymentMethodProveedor = async ({
  id,
  data,
}: {
  id: string;
  data: CreatePaymentMethodProveedorProps;
}) => {
  //const setAlert = useUiStore.getState().setAlert;
  const res = await put<IPaymentMethodProveedor>(
    `${urlIApiPaymentMethodProveedor}${id}/`,
    data,
    true
  );

  //setAlert(true, res.message, res.status);
  return res;
};

export const removePaymentMethodProveedor = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<IPaymentMethodProveedor>(
    `${urlIApiPaymentMethodProveedor}${id}/`,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
